/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Boundary refinement — re-anchor the model's timestamps onto the real audio.
 *
 * WHY THIS EXISTS
 * ---------------
 * Audio LLMs are good at *what* was said and unreliable about *when*. Measured
 * on a 56 s file of 60 isolated words, gemini-2.5-flash returned a near-uniform
 * 0.8 s grid while the speaker's real cadence was ~0.93 s/word. The timestamps
 * therefore ran progressively EARLY: −0.34 s at the first word, +6.7 s by the
 * last. Cutting those windows faithfully (which the slicer does, to the sample)
 * yields a clip labelled word N that actually contains word N−7. That is the
 * "the audio is 3-4 clips behind the label" bug.
 *
 * The model's *ordering* and *word count* are sound; only its time axis is
 * wrong, and it is wrong smoothly. So we do not need to re-transcribe — we need
 * to re-project the model's timeline onto the audio's own timeline.
 *
 * HOW
 * ---
 * 1. ENVELOPE + VAD. Short-window RMS over the decoded buffer, then a speech
 *    mask via hysteresis thresholding against the file's own noise floor.
 *
 * 2. GLOBAL SPEECH-TIME WARP. Both timelines agree on how much *speech* there
 *    is, just not where it sits. Map a model time t to the audio time at which
 *    the same fraction of cumulative speech has elapsed. This removes the bulk
 *    of the drift (measured: mean |error| 3.20 s -> 0.33 s) and is O(n).
 *
 * 3. BANDED DTW. The warp is a good global estimate but still off by up to a
 *    word. A dynamic-time-warp between the model's coverage signal (1 inside a
 *    segment, 0 in a gap) and the audio's speech mask fixes the rest. The
 *    search is confined to a narrow band around the stage-2 warp, which is what
 *    makes it affordable: ~0.6 MB and a few million cells for a one-minute file
 *    instead of the ~31 MB an unbanded DTW would need.
 *    (measured: mean |error| 0.33 s -> 0.019 s, worst 0.10 s)
 *
 * 4. SNAP + REPAIR. Nudge each boundary onto the nearest speech edge, then
 *    enforce order, non-overlap and a minimum duration.
 *
 * 5. ONSET ANCHORING. Stages 2–4 only ever snap to SILENCE, so inside a burst of
 *    continuous speech they leave the model's proportional splits exactly where
 *    they were — which is where this pipeline's remaining error lives. Stage 5
 *    detects spectral onsets inside each burst and lays the burst's segments
 *    onto them by dynamic programming. See services/legacy/onsetAlign.ts.
 *
 * 6. GUARD. Score how much of each segment's span actually lands on speech,
 *    before and after. If refinement did not improve that score, the original
 *    timings are returned untouched — a bad alignment must never be able to
 *    make things worse than the model's own guess. Stage 5 carries its own,
 *    finer-grained guard: it is reverted per burst, not wholesale.
 */

import { round3 } from '../../lib/num';
import type { RawSegment } from '../../types';
import { extractMono } from '../audioSlicer';
import { alignToOnsets, type OnsetAlignResult } from './onsetAlign';
import { detectOnsets } from '../onsetDetect';
import { computeSpeechEnvelope, findSpeechBursts, snapToEdge, type SpeechEnvelope } from '../vad';

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

export interface RefineOptions {
  /** RMS envelope hop, seconds. */
  hopSec?: number;
  /** RMS envelope window, seconds. */
  winSec?: number;
  /** Frame size for the DTW grid, seconds. Coarser than the envelope. */
  dtwHopSec?: number;
  /** Half-width of the DTW search band around the global warp, seconds. */
  bandSec?: number;
  /** Final local snap radius onto a speech edge, seconds. */
  snapSec?: number;
  /** Segments are never shortened below this, seconds. */
  minSegmentSec?: number;
  /** Run stage 5 (onset anchoring). Default true. */
  onsetAnchoring?: boolean;
  /** Passed straight to {@link alignToOnsets}. */
  onsetOptions?: Parameters<typeof alignToOnsets>[2];
}

/*
 * Stage 2–5 geometry only. `hopSec`/`winSec` moved to `services/vad.ts` with
 * the envelope that reads them; `RefineOptions` still forwards them there.
 */
const DEFAULTS = {
  dtwHopSec: 0.02,
  bandSec: 2.5,
  snapSec: 0.15,
  minSegmentSec: 0.06,
} as const;

/** Cost charged for a non-diagonal DTW step, per frame of time skew. */
const DTW_SKIP_COST = 0.02;

/** Keep the DTW grid this side of a phone's memory budget. */
const MAX_DTW_ROWS = 30000;

export interface RefineResult {
  /** Refined segments, or the input unchanged when `applied` is false. */
  segments: RawSegment[];
  /** Whether the refinement was accepted. */
  applied: boolean;
  /** Why it was skipped, when it was. */
  reason?: string;
  /** Mean |newStart - oldStart| across segments, seconds. */
  meanShiftSec: number;
  /** Largest single boundary move, seconds. */
  maxShiftSec: number;
  /** Fraction of segment span landing on detected speech, before/after. */
  coverageBefore: number;
  coverageAfter: number;
  /**
   * Boundaries after stage 2 (global speech-time warp) alone, before the DTW
   * and the snap. Diagnostic only — nothing in the pipeline consumes it.
   *
   * Comparing it with `segments` separates the two failure modes that look
   * identical in the final output: bulk drift that the warp should have removed,
   * versus local misalignment inside a burst that only the DTW can fix. Absent
   * when refinement bailed before the warp was built.
   */
  warpOnly?: RawSegment[];
  /** Whether the banded DTW found a path; false means the warp was used alone. */
  dtwApplied?: boolean;
  /**
   * Boundaries after stage 4 (DTW + snap) but before stage 5 anchored them onto
   * acoustic onsets. Diagnostic, and the thing to diff against `segments` when a
   * burst comes back wrong.
   */
  beforeOnsets?: RawSegment[];
  /** What stage 5 did, per burst. Absent when it was skipped. */
  onsets?: OnsetAlignResult<RawSegment>;
}

/** Fraction of the segments' total span that lands on detected speech. */
function coverageScore(segments: RawSegment[], env: SpeechEnvelope): number {
  let span = 0;
  let hit = 0;
  for (const s of segments) {
    const from = Math.max(0, Math.floor(s.startSec / env.hopSec));
    const to = Math.min(env.mask.length, Math.ceil(s.endSec / env.hopSec));
    for (let i = from; i < to; i++) {
      span++;
      if (env.mask[i]) hit++;
    }
  }
  return span > 0 ? hit / span : 0;
}

/* ------------------------------------------------------------------ */
/* 2 — global speech-time warp                                         */
/* ------------------------------------------------------------------ */

/**
 * Map model time -> audio time by equalising elapsed speech.
 *
 * `cumCovered(t)` is how many seconds of segment the model has spent by time
 * `t`; scaling it by `k` puts it on the audio's speech budget, and the inverse
 * lookup asks "at what real time had the audio produced that much speech?".
 * Monotone by construction, so it can never reorder segments.
 */
function buildGlobalWarp(
  segments: RawSegment[],
  env: SpeechEnvelope,
): ((t: number) => number) | null {
  const speech = env.speechFrames;
  if (speech.length === 0) return null;

  const totalAudioSpeech = speech.length * env.hopSec;
  let totalModelSpeech = 0;
  const cumBefore = new Float64Array(segments.length);
  for (let i = 0; i < segments.length; i++) {
    cumBefore[i] = totalModelSpeech;
    totalModelSpeech += Math.max(0, segments[i].endSec - segments[i].startSec);
  }
  if (totalModelSpeech <= 0) return null;
  const k = totalAudioSpeech / totalModelSpeech;

  const cumCovered = (t: number): number => {
    // segments are time-ordered, so a linear scan with an early exit is fine
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      if (t <= s.startSec) return cumBefore[i];
      if (t < s.endSec) return cumBefore[i] + (t - s.startSec);
    }
    return totalModelSpeech;
  };

  return (t: number): number => {
    const c = cumCovered(t) * k;
    const q = Math.min(speech.length - 1, Math.max(0, Math.floor(c / env.hopSec)));
    return speech[q] * env.hopSec;
  };
}

/* ------------------------------------------------------------------ */
/* 3 — banded DTW                                                      */
/* ------------------------------------------------------------------ */

/**
 * Align the model's coverage signal to the audio's speech mask.
 *
 * Returns a frame-to-frame mapping (model grid -> audio grid), or null when the
 * band turned out to be too narrow for a path to exist.
 */
function alignByDtw(
  segments: RawSegment[],
  env: SpeechEnvelope,
  warp: (t: number) => number,
  audioDurSec: number,
  dtwHopSec: number,
  bandSec: number,
): ((t: number) => number) | null {
  const H = dtwHopSec;
  const modelDur = segments[segments.length - 1].endSec;
  const rows = Math.ceil(modelDur / H) + 1;
  const cols = Math.ceil(audioDurSec / H) + 1;
  const band = Math.max(2, Math.round(bandSec / H));
  const width = 2 * band + 1;

  // model coverage signal, 1 inside a segment and 0 in a gap
  const A = new Uint8Array(rows);
  for (const s of segments) {
    const from = Math.min(rows - 1, Math.max(0, Math.floor(s.startSec / H)));
    const to = Math.min(rows, Math.max(from + 1, Math.ceil(s.endSec / H)));
    A.fill(1, from, to);
  }
  // audio speech mask resampled onto the DTW grid
  const B = new Uint8Array(cols);
  for (let j = 0; j < cols; j++) {
    const f = Math.min(env.mask.length - 1, Math.floor((j * H) / env.hopSec));
    B[j] = env.mask[f];
  }

  /*
   * Band centres follow the global warp, but the warp jumps whenever the model
   * crosses a gap that the audio spends a long silence on. A jump wider than
   * the band would leave a row with no reachable predecessor and kill the path,
   * so the centre is slope-limited. The limit is self-correcting: the centre
   * catches up on the following rows, because it only ever lags the warp.
   */
  const ctr = new Int32Array(rows + 1);
  let prevCtr = 0;
  for (let i = 0; i <= rows; i++) {
    const target = Math.min(cols, Math.max(0, Math.round(warp(i * H) / H)));
    const stepped = Math.min(target, prevCtr + band);
    ctr[i] = Math.max(prevCtr, stepped);
    prevCtr = ctr[i];
  }

  const INF = Number.POSITIVE_INFINITY;
  const ptr = new Int8Array((rows + 1) * width);
  let prev = new Float64Array(width).fill(INF);
  let cur = new Float64Array(width).fill(INF);

  const start = 0 - ctr[0] + band;
  if (start < 0 || start >= width) return null;
  prev[start] = 0;

  for (let i = 1; i <= rows; i++) {
    cur.fill(INF);
    const lo = Math.max(1, ctr[i] - band);
    const hi = Math.min(cols, ctr[i] + band);
    const ai = A[i - 1];
    const rowBase = i * width;
    const cPrev = ctr[i - 1];
    const cCur = ctr[i];
    for (let j = lo; j <= hi; j++) {
      const d = ai === B[j - 1] ? 0 : 1;
      let best = INF;
      let step = 0;

      let o = j - 1 - cPrev + band;
      if (o >= 0 && o < width && prev[o] < best) {
        best = prev[o];
        step = 0;
      }
      o = j - cPrev + band;
      if (o >= 0 && o < width && prev[o] + DTW_SKIP_COST < best) {
        best = prev[o] + DTW_SKIP_COST;
        step = 1;
      }
      o = j - 1 - cCur + band;
      if (o >= 0 && o < width && cur[o] + DTW_SKIP_COST < best) {
        best = cur[o] + DTW_SKIP_COST;
        step = 2;
      }
      if (best === INF) continue;

      const slot = j - cCur + band;
      cur[slot] = best + d;
      ptr[rowBase + slot] = step;
    }
    const swap = prev;
    prev = cur;
    cur = swap;
  }

  const endSlot = cols - ctr[rows] + band;
  if (endSlot < 0 || endSlot >= width || prev[endSlot] === INF) return null;

  // backtrack into a model-frame -> audio-frame table
  const map = new Float64Array(rows + 1).fill(-1);
  let i = rows;
  let j = cols;
  while (i > 0 && j > 0) {
    map[i] = j;
    const step = ptr[i * width + (j - ctr[i] + band)];
    if (step === 0) {
      i--;
      j--;
    } else if (step === 1) {
      i--;
    } else {
      j--;
    }
  }
  map[0] = Math.max(0, j);
  for (let n = 1; n <= rows; n++) if (map[n] < 0) map[n] = map[n - 1];

  return (t: number): number => {
    const f = t / H;
    const i0 = Math.min(rows - 1, Math.max(0, Math.floor(f)));
    const frac = f - i0;
    return (map[i0] + (map[i0 + 1] - map[i0]) * frac) * H;
  };
}


/* ------------------------------------------------------------------ */
/* Public entry point                                                  */
/* ------------------------------------------------------------------ */

/**
 * Re-anchor `segments` onto `buffer`'s real timeline.
 *
 * Pure and side-effect free: returns a new array and never mutates the input.
 * Text, kind and confidence are carried through untouched — only `startSec` and
 * `endSec` are ever changed.
 */
export function refineBoundaries(
  buffer: AudioBuffer,
  segments: RawSegment[],
  options: RefineOptions = {},
): RefineResult {
  const skip = (reason: string, coverage = 0): RefineResult => ({
    segments,
    applied: false,
    reason,
    meanShiftSec: 0,
    maxShiftSec: 0,
    coverageBefore: coverage,
    coverageAfter: coverage,
  });

  if (segments.length === 0) return skip('no segments');

  const duration = buffer.duration;
  if (!Number.isFinite(duration) || duration <= 0) return skip('no duration');

  const minSegmentSec = options.minSegmentSec ?? DEFAULTS.minSegmentSec;
  const bandSec = options.bandSec ?? DEFAULTS.bandSec;
  const snapSec = options.snapSec ?? DEFAULTS.snapSec;

  const mono = extractMono(buffer, 0, duration);
  const env = computeSpeechEnvelope(mono, buffer.sampleRate, options);

  const speechRatio = env.speechFrames.length / Math.max(1, env.mask.length);
  // Nothing to align against (silence), or no silence to align with (a mask
  // that covers everything carries no positional information at all).
  if (speechRatio < 0.02) return skip('almost no speech detected');
  if (speechRatio > 0.98) return skip('no silence to align against');

  const coverageBefore = coverageScore(segments, env);

  const warp = buildGlobalWarp(segments, env);
  if (!warp) return skip('could not build a global warp', coverageBefore);

  // Keep the DTW grid bounded on long files.
  const modelDur = segments[segments.length - 1].endSec;
  const dtwHopSec = Math.max(
    options.dtwHopSec ?? DEFAULTS.dtwHopSec,
    Math.max(modelDur, duration) / MAX_DTW_ROWS,
  );

  const align = alignByDtw(segments, env, warp, duration, dtwHopSec, bandSec);
  // The warp alone is still a large improvement, so fall back to it rather than
  // giving up entirely when the banded search finds no path.
  const project = align ?? warp;

  // Stage-2-only projection, kept for the run log. Monotone and unsnapped, so
  // it needs no repair pass — it is never used as output.
  const warpOnly: RawSegment[] = segments.map((s) => ({
    ...s,
    startSec: round3(warp(s.startSec)),
    endSec: round3(warp(s.endSec)),
  }));

  const out: RawSegment[] = [];
  let cursor = 0;
  let shiftSum = 0;
  let shiftMax = 0;

  for (const s of segments) {
    let start = project(s.startSec);
    let end = project(s.endSec);

    start = snapToEdge(start, env, snapSec, true);
    end = snapToEdge(end, env, snapSec, false);

    // order, non-overlap, minimum length, inside the file
    start = Math.min(Math.max(start, cursor), Math.max(0, duration - minSegmentSec));
    end = Math.max(end, start + minSegmentSec);
    if (end > duration) {
      end = duration;
      start = Math.min(start, Math.max(0, end - minSegmentSec));
    }
    cursor = end;

    const shift = Math.abs(start - s.startSec);
    shiftSum += shift;
    if (shift > shiftMax) shiftMax = shift;

    out.push({ ...s, startSec: round3(start), endSec: round3(end) });
  }

  const coverageAfter = coverageScore(out, env);
  if (coverageAfter < coverageBefore) {
    return {
      ...skip('refinement did not improve speech coverage', coverageBefore),
      coverageAfter,
      warpOnly,
      dtwApplied: align !== null,
    };
  }

  /* -- 5. onset anchoring inside each burst ------------------------- */
  /*
   * Everything above this line only ever moved a boundary onto a SILENCE edge.
   * Inside a burst there is none, so the model's proportional splits survive
   * stages 2–4 untouched — and that is where the remaining error is. Stage 5 is
   * the only part of refinement that can see into a burst.
   *
   * It guards itself per burst (a burst whose local span/recall/plausibility
   * score would drop keeps its old boundaries), so there is no wholesale bail
   * here: `final` is at worst identical to `out`.
   */
  let final = out;
  let onsetResult: OnsetAlignResult<RawSegment> | undefined;
  if (options.onsetAnchoring !== false) {
    try {
      const bursts = findSpeechBursts(env);
      const { onsets } = detectOnsets(mono, buffer.sampleRate);
      onsetResult = alignToOnsets<RawSegment>(
        out,
        { env, bursts, onsets, durationSec: duration },
        options.onsetOptions,
      );
      if (onsetResult.applied) final = onsetResult.segments;
    } catch (err) {
      // Onset detection is an optimisation, not a requirement. A failure here
      // must leave the DTW result standing rather than fail the run.
      console.warn('[lableforge] onset anchoring failed, keeping the DTW boundaries:', err);
    }
  }

  return {
    segments: final,
    applied: true,
    meanShiftSec: shiftSum / out.length,
    maxShiftSec: shiftMax,
    coverageBefore,
    coverageAfter: coverageScore(final, env),
    warpOnly,
    dtwApplied: align !== null,
    beforeOnsets: onsetResult?.applied ? out : undefined,
    onsets: onsetResult,
  };
}

export default refineBoundaries;
